import type { AxiosError } from 'axios';
import type { KeyedMutator, SWRConfiguration } from 'swr';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';

// Types
import type { filtersStateType } from '@/components/pages/notifications/types';
import type { NotificationType } from '@/hooks/context/toast/types';

// Routes
import { NOTIFICATIONS } from '@/routes/apiEndpoints';

export type dataNotificationsProps = {
    notifications: NotificationType[];
    total_unread: number;
    total_count: number;
    cursor: string;
};

type getKeyProps = (
    pageIndex: number,
    previousPageData: dataNotificationsProps,
    params: filtersStateType
) => Record<number, string>;

const getKey: getKeyProps = (pageIndex, previousPageData, params) => {
    // No request params
    if (!params) return null;
    // Reached the end
    if (previousPageData && !previousPageData?.notifications) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [NOTIFICATIONS, JSON.stringify(params)];
    }

    // Add the cursor to the API endpoint
    return [`${NOTIFICATIONS}?cursor=${previousPageData.cursor}`, JSON.stringify(params)];
};

export type mutateNotificationsType = KeyedMutator<dataNotificationsProps[]>;
export type loadMoreNotificationsType = () => Promise<dataNotificationsProps[]>;

type OptionsConfiguration = SWRConfiguration<dataNotificationsProps, AxiosError<Error>> &
    SWRInfiniteConfiguration<dataNotificationsProps, AxiosError<Error>>;

const useNotifications = (params: filtersStateType, options?: OptionsConfiguration) => {
    const {
        data: dataNotifications,
        size,
        setSize,
        mutate: mutateNotifications,
        error: notificationsError
    } = useSWRInfinite<dataNotificationsProps, AxiosError<Error>>(
        (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, params),
        ([url]) => get(url, params).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const total_unread = dataNotifications?.[0]?.total_unread;
    const total_count = dataNotifications?.[0]?.total_count;
    const isLoadingInitialData = !dataNotifications && !notificationsError;
    const isLoadingMore =
        size > 0 && dataNotifications && typeof dataNotifications[size - 1] === 'undefined';

    return {
        dataNotifications,
        total_unread: total_unread,
        total: total_count,
        mutateNotifications,
        notificationsError,
        loadMoreNotifications: () => setSize(size + 1), // Function to load more notifications (load more button)
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataNotifications &&
            typeof dataNotifications[dataNotifications.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};
export default useNotifications;
